import { useMemo } from 'react'
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FileDownloadTwoTone } from '@mui/icons-material'
import { useMeasurementUnits } from '~/common/use-measurement-units'
import { TpTooltip } from '~/components'
import { useHarvestSummary } from './use-harvest-summary'

const CURRENT_YEAR = new Date().getFullYear()

const totalRowCellSx = {
  fontWeight: 700,
  bgcolor: ({ palette }) => palette.mode === 'dark' ? palette.grey[800] : palette.grey[200],
}

const downloadCSV = (harvestSummary) => {
  const header = 'Crop,Plants,Successions,Harvests,Yield (kg)'
  const rows = harvestSummary.map(item => [
    `"${item.name.replace(/"/g, '""')}"`,
    item.plantCount,
    item.successionCount,
    item.harvestCount,
    item.totalYield ?? '',
  ].join(','))
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `terraplan-crops-${CURRENT_YEAR}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Standalone crop harvest summary table.
 * Fills available vertical space, clamped between minHeight and maxHeight.
 * Sticky header always visible; body scrolls when rows overflow.
 *
 * @param {Object[]} crops - From useCropData()
 * @param {number} minHeight - Min total component height in px (default 400, pass 0 to disable)
 * @param {number} maxHeight - Max total component height in px (default 600, pass undefined to disable)
 */
export const CropHarvestTable = ({ crops, minHeight = 400, maxHeight = 600 }) => {
  const theme = useTheme()
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'))
  const { summary: harvestSummary, loading } = useHarvestSummary(crops)
  const { formatMass } = useMeasurementUnits()

  const totals = useMemo(() => {
    const plants = harvestSummary.reduce((s, r) => s + (r.plantCount || 0), 0)
    const successions = harvestSummary.reduce((s, r) => s + (r.successionCount || 0), 0)
    const harvests = harvestSummary.reduce((s, r) => s + (r.harvestCount || 0), 0)
    const yieldRows = harvestSummary.filter(r => r.totalYield != null)
    const yield_ = yieldRows.length ? yieldRows.reduce((s, r) => s + r.totalYield, 0) : null
    return { plants, successions, harvests, yield_ }
  }, [harvestSummary])

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: minHeight || undefined,
        maxHeight: maxHeight || undefined,
      }}
    >
      {/* Section header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          CROP HARVEST
        </Typography>
        {isSmUp ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadTwoTone />}
            sx={{ textTransform: 'none', minWidth: '122px', flexShrink: 0 }}
            onClick={() => downloadCSV(harvestSummary)}
            disabled={harvestSummary.length === 0}
          >
            Export CSV
          </Button>
        ) : (
          <TpTooltip title="Export CSV">
            <IconButton
              onClick={() => downloadCSV(harvestSummary)}
              color="primary"
              disabled={harvestSummary.length === 0}
            >
              <FileDownloadTwoTone />
            </IconButton>
          </TpTooltip>
        )}
      </Box>

      {loading && <LinearProgress />}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  color: ({ palette }) => palette.mode === 'dark' ? palette.text.primary : palette.text.primary,
                  fontWeight: 600,
                  backgroundColor: ({ palette }) =>palette.mode === 'dark' ? palette.grey[700] : palette.grey[300],
                },
              }}
            >
              <TableCell>Crop</TableCell>
              <TableCell align="right">Plants</TableCell>
              <TableCell align="right">Successions</TableCell>
              <TableCell align="right">Harvests</TableCell>
              <TableCell align="right">Yield Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {harvestSummary.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell align="right">{item.plantCount}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: item.successionCount != '' ? 'text.primary' : 'text.disabled',
                    fontWeight: item.successionCount != '' ? 600 : 400,
                  }}
                >
                  {item.successionCount || '—'}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: item.harvestCount != '' ? 'success.main' : 'text.disabled',
                    fontWeight: item.harvestCount != '' ? 600 : 400,
                  }}
                >
                  {item.harvestCount || '—'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: item.totalYield != null ? 'success.main' : 'text.disabled',
                    fontWeight: item.totalYield != null ? 600 : 400,
                  }}
                >
                  {item.totalYield != null ? formatMass(item.totalYield) : '—'}
                </TableCell>
              </TableRow>
            ))}
            
            <TableRow>
              <TableCell sx={totalRowCellSx}>Total</TableCell>
              <TableCell align="right" sx={totalRowCellSx}>{totals.plants}</TableCell>
              <TableCell align="right" sx={[totalRowCellSx, { color: totals.successions ? 'text.primary' : 'text.disabled' }]}>{totals.successions || '—'}</TableCell>
              <TableCell align="right" sx={[totalRowCellSx, { color: totals.harvests ? 'success.main' : 'text.disabled' }]}>{totals.harvests || '—'}</TableCell>
              <TableCell align="right" sx={[totalRowCellSx, { color: totals.yield_ != null ? 'success.main' : 'text.disabled' }]}>{totals.yield_ != null ? formatMass(totals.yield_) : '—'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}
